import { type Either } from '@/core/either'
import { useGlobalLoading } from '@/composables/useGlobalLoading'
import type { AxiosInstance } from 'axios'
import type { ApiError } from './errors'
import type {
  Activity,
  AttestToProductConformityResponse,
  GetManufacturingOrderResponse,
  GetProductForManufacturingOrderResponse,
  GetReadProductResponse,
  HealthResponse,
  ListNonConformityReasonsResponse,
  ListProductionDataResponse,
  ListShippingReceiptResponse,
  ListShippingResponse,
  ListWebhooksByActivityIdResponse,
  OperatorLoginResponse
} from './types'
import {
  handleAxiosResponse,
  initService,
  setRequestInterceptors,
  setResponseInterceptors
} from './utils'

class WarrantyApiService {
  private static httpClient: AxiosInstance

  public static init(): AxiosInstance {
    this.httpClient = initService()
    setResponseInterceptors(this.httpClient)
    setRequestInterceptors(this.httpClient)

    return this.httpClient
  }

  private static get getClient() {
    let httpClient = this.httpClient

    if (!this.httpClient) {
      httpClient = this.init()
    }
    return httpClient
  }

  public static async healthCheck(): Promise<Either<ApiError, HealthResponse>> {
    const url = `${'/health'}`
    const request = this.getClient.get(url)

    return handleAxiosResponse<HealthResponse>(request)
  }

  public static async getTagInfo(tag: string): Promise<Either<ApiError, any>> {
    const url = `${'/get-tag-info'}?tagCode=${tag}`
    const request = this.getClient.get(url)

    return handleAxiosResponse<any>(request)
  }

  public static async getManufacturingOrder(
    orderNumber: string
  ): Promise<Either<ApiError, GetManufacturingOrderResponse>> {
    const url = `${'/get-manufacturing-order'}/${orderNumber}`
    const request = this.getClient.get(url)

    return handleAxiosResponse<GetManufacturingOrderResponse>(request)
  }

  public static async getReadProduct(
    productCode: string
  ): Promise<Either<ApiError, GetReadProductResponse>> {
    const url = `${'/get-product-unit-by-tag-code'}`
    const request = this.getClient.post(url, { tagCode: productCode })

    return handleAxiosResponse<GetReadProductResponse>(request)
  }

  public static async getProductForManufacturingOrder(
    tagCode: string,
    productionOrderId: string,
    activityId: string
  ): Promise<Either<ApiError, GetProductForManufacturingOrderResponse>> {
    const url = `/get-product-for-manufacturing-order?tagCode=${tagCode}&manufacturingOrderId=${productionOrderId}&activityId=${activityId}`
    const request = this.getClient.get(url)

    return handleAxiosResponse<GetProductForManufacturingOrderResponse>(request)
  }

  public static async attestToProductConformity(input: {
    serialNumber: string
    tagCode: string
    manufacturingOrderId: string
    productUnitId: string
    activityId: string
    duplicatedFlyerConfirmed?: boolean
  }): Promise<Either<ApiError, AttestToProductConformityResponse>> {
    const { showGlobalLoading, hideGlobalLoading } = useGlobalLoading()
    const url = '/attest-to-product-conformity'
    showGlobalLoading()
    try {
      const request = this.getClient.post(url, input)
      return await handleAxiosResponse<AttestToProductConformityResponse>(request)
    } finally {
      hideGlobalLoading()
    }
  }

  public static async listWebhooksByActivityId(
    activityId: string
  ): Promise<Either<ApiError, ListWebhooksByActivityIdResponse>> {
    const url = `/list-webhooks-by-activity-id/${activityId}`
    const request = this.getClient.get(url)

    return handleAxiosResponse<ListWebhooksByActivityIdResponse>(request)
  }

  public static async reexecuteWebhook(
    tagCode: string,
    webhookId: string
  ): Promise<Either<ApiError, void>> {
    const url = `/reexecute-webhook`
    const request = this.getClient.post(url, {
      tagCode,
      webhookId
    })

    return handleAxiosResponse<void>(request)
  }

  public static async listNonConformityReasons(input: {
    search?: string
    page?: number
    limit?: number
  }) {
    let url = '/list-non-conformity-reasons?'
    if (input.search) {
      url += `search=${input.search}&`
    }
    if (input.page) {
      url += `page=${input.page}&`
    }
    if (input.limit) {
      url += `limit=${input.limit}&`
    }
    const request = this.getClient.get(url)

    return handleAxiosResponse<ListNonConformityReasonsResponse>(request)
  }

  public static async listActivity(input: { operationId: string; tagCode: string }) {
    let url = '/list-activity'
    if (input.operationId) {
      url += `?operationId=${input.operationId}`
    }
    if (input.tagCode) {
      url += `&tagCode=${input.tagCode}`
    }
    const request = this.getClient.get(url)

    return handleAxiosResponse<Activity[]>(request)
  }

  public static async registerProductNonConformity(input: {
    manufacturingOrderId: string
    tagCode: string
    activityId: string
    nonConformityReasonIds: string[]
  }) {
    const url = '/register-product-non-conformity'
    const request = this.getClient.post(url, input)

    return handleAxiosResponse<undefined>(request)
  }

  public static async operatorLogin(input: {
    accessKey: string
  }): Promise<Either<ApiError, OperatorLoginResponse>> {
    const url = '/operator-login'
    const request = this.getClient.post(url, input)

    return handleAxiosResponse<OperatorLoginResponse>(request)
  }

  public static async listProductionData(): Promise<Either<ApiError, ListProductionDataResponse>> {
    const url = '/list-production-data'
    const request = this.getClient.get(url)
    return await handleAxiosResponse<ListProductionDataResponse>(request)
  }

  public static async listShipping(): Promise<Either<ApiError, ListShippingResponse>> {
    const url = '/list-shipping'
    const request = this.getClient.get(url)

    return handleAxiosResponse<ListShippingResponse>(request)
  }

  public static async listShippingReceipt(
    shippingId: string
  ): Promise<Either<ApiError, ListShippingReceiptResponse>> {
    const url = `/list-shipping-receipt-by-shipping-id/${shippingId}`
    const request = this.getClient.get(url)

    return handleAxiosResponse<ListShippingReceiptResponse>(request)
  }

  public static async addTagToShipping(input: {
    tagSerialNumberOrProductSku: string
    carrierTagId?: string
    shippingReceiptId: string
    activityId: string
    shippingId: string
    productionSequenceOperationId: string
  }): Promise<Either<ApiError, void>> {
    const { showGlobalLoading, hideGlobalLoading } = useGlobalLoading()
    const url = '/add-tag-to-shipping'
    showGlobalLoading()
    try {
      const request = this.getClient.post(url, input)
      return await handleAxiosResponse<void>(request)
    } finally {
      hideGlobalLoading()
    }
  }

  public static async attestToShippingVerification(input: {
    id: string
  }): Promise<Either<ApiError, void>> {
    const url = '/attest-to-shipping-verification'
    const request = this.getClient.post(url, input)

    return handleAxiosResponse<void>(request)
  }

  public static async checkToken(): Promise<Either<ApiError, void>> {
    const url = '/operator/check-token'
    const request = this.getClient.get(url)

    return handleAxiosResponse<void>(request)
  }
}

export default WarrantyApiService
